import { useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AdminTable, type ColDef } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import {
  PageHeader,
  AddButton,
  SaveButton,
  CancelButton,
  ViewButton,
  EditButton,
  DeleteButton,
} from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { TableSearch } from "@/components/admin/shared/TableSearch";
import { PennField, PennInput, PennDropdown } from "@/components/ui";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import {
  useSherexFasteners,
  useSherexFastener,
  useSherexFastenerOptions,
  useCreateSherexFastener,
  useDeleteSherexFastener,
  useUpdateSherexFastener,
} from "@/hooks/useSherexFasteners";
import type {
  SherexEndType,
  SherexFastener,
  SherexMaterial,
  SherexTools,
} from "@/api/sherexFasteners";

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

const MATERIAL_LABELS: Record<SherexMaterial, string> = {
  aluminum: "Aluminum",
  steel: "Steel",
  high_strength_steel: "High Strength Steel",
  stainless_steel: "Stainless Steel",
  copper: "Copper",
  nickel_copper_alloy: "Nickel Copper Alloy",
  monel: "Monel",
};

const END_TYPE_LABELS: Record<SherexEndType, string> = {
  blind: "Blind",
  open: "Open",
};

const TOOL_LABELS: Record<keyof SherexTools, string> = {
  flex5: "FLEX-5",
  flex18: "FLEX-18",
  riv943: "RIV943",
  riv949: "RIV949",
  ssg800Au: "SSG-800 [AU]",
  ssg800Av: "SSG-800 [AV]",
};
const TOOL_KEYS: (keyof SherexTools)[] = [
  "flex5",
  "flex18",
  "riv943",
  "riv949",
  "ssg800Au",
  "ssg800Av",
];

const numStr = z
  .union([z.literal(""), z.coerce.number().nonnegative("Must be non-negative")])
  .optional();

const toolsSchema = z.object({
  flex5: z.boolean().optional().default(false),
  flex18: z.boolean().optional().default(false),
  riv943: z.boolean().optional().default(false),
  riv949: z.boolean().optional().default(false),
  ssg800Au: z.boolean().optional().default(false),
  ssg800Av: z.boolean().optional().default(false),
});

const sherexFormSchema = z
  .object({
    partNumber: z.string().trim().min(1, "Part Number is required").max(50),
    rivetNutType: z.string().trim().optional(),
    series: z.string().trim().optional(),
    material: z.string().trim().optional(), // empty -> null
    endType: z.string().trim().optional(),
    unitSystem: z.enum(["us", "metric"]).default("us"),
    // Diameter holds thread designations (e.g. "M5x08", "06-32"), not numbers.
    diameterUs: z.string().trim().optional(),
    diameterMetric: z.string().trim().optional(),
    minHoleSizeUs: numStr,
    minHoleSizeMetric: numStr,
    maxHoleSizeUs: numStr,
    maxHoleSizeMetric: numStr,
    minGripRangeUs: numStr,
    minGripRangeMetric: numStr,
    maxGripRangeUs: numStr,
    maxGripRangeMetric: numStr,
    bodyLengthUs: numStr,
    bodyLengthMetric: numStr,
    fullLengthUs: numStr,
    fullLengthMetric: numStr,
    flangeDiameterUs: numStr,
    flangeDiameterMetric: numStr,
    flangeThicknessUs: numStr,
    flangeThicknessMetric: numStr,
    installedLengthUs: numStr,
    installedLengthMetric: numStr,
    shearStrengthUsLbf: numStr,
    shearStrengthMetricKn: numStr,
    tensileStrengthUsLbf: numStr,
    tensileStrengthMetricKn: numStr,
    radialDeflectionInch: numStr,
    radialDeflectionMm: numStr,
    tools: toolsSchema,
    minRecommendedInstallForceLbs: numStr,
    maxRecommendedInstallForceLbs: numStr,
  })
  .refine(
    (v) =>
      v.minRecommendedInstallForceLbs === "" ||
      v.minRecommendedInstallForceLbs === undefined ||
      v.maxRecommendedInstallForceLbs === "" ||
      v.maxRecommendedInstallForceLbs === undefined ||
      Number(v.minRecommendedInstallForceLbs) <=
        Number(v.maxRecommendedInstallForceLbs),
    {
      message: "Min force must be ≤ Max force",
      path: ["minRecommendedInstallForceLbs"],
    },
  );

type SherexFormData = z.infer<typeof sherexFormSchema>;

const blank: SherexFormData = {
  partNumber: "",
  rivetNutType: "",
  series: "",
  material: "",
  endType: "blind", // new records default to Blind (user can switch to Open)
  unitSystem: "us",
  diameterUs: "",
  diameterMetric: "",
  minHoleSizeUs: "",
  minHoleSizeMetric: "",
  maxHoleSizeUs: "",
  maxHoleSizeMetric: "",
  minGripRangeUs: "",
  minGripRangeMetric: "",
  maxGripRangeUs: "",
  maxGripRangeMetric: "",
  bodyLengthUs: "",
  bodyLengthMetric: "",
  fullLengthUs: "",
  fullLengthMetric: "",
  flangeDiameterUs: "",
  flangeDiameterMetric: "",
  flangeThicknessUs: "",
  flangeThicknessMetric: "",
  installedLengthUs: "",
  installedLengthMetric: "",
  shearStrengthUsLbf: "",
  shearStrengthMetricKn: "",
  tensileStrengthUsLbf: "",
  tensileStrengthMetricKn: "",
  radialDeflectionInch: "",
  radialDeflectionMm: "",
  tools: {
    flex5: false,
    flex18: false,
    riv943: false,
    riv949: false,
    ssg800Au: false,
    ssg800Av: false,
  },
  minRecommendedInstallForceLbs: "",
  maxRecommendedInstallForceLbs: "",
};

// Records are stored as either US or Metric (the other unit's columns are
// null). Detect which set a row actually carries so edit/view show the right
// unit + values instead of always defaulting to US.
const US_VALUE_FIELDS: (keyof SherexFastener)[] = [
  "diameterUs",
  "minHoleSizeUs",
  "maxHoleSizeUs",
  "minGripRangeUs",
  "maxGripRangeUs",
  "bodyLengthUs",
  "fullLengthUs",
  "flangeDiameterUs",
  "flangeThicknessUs",
  "installedLengthUs",
  "shearStrengthUsLbf",
  "tensileStrengthUsLbf",
  "radialDeflectionInch",
];
const METRIC_VALUE_FIELDS: (keyof SherexFastener)[] = [
  "diameterMetric",
  "minHoleSizeMetric",
  "maxHoleSizeMetric",
  "minGripRangeMetric",
  "maxGripRangeMetric",
  "bodyLengthMetric",
  "fullLengthMetric",
  "flangeDiameterMetric",
  "flangeThicknessMetric",
  "installedLengthMetric",
  "shearStrengthMetricKn",
  "tensileStrengthMetricKn",
  "radialDeflectionMm",
];

const detectUnitSystem = (item: SherexFastener): "us" | "metric" => {
  const populated = (fields: (keyof SherexFastener)[]) =>
    fields.filter((f) => {
      const v = item[f];
      return v !== null && v !== undefined && v !== "";
    }).length;
  // Tie or empty defaults to US.
  return populated(METRIC_VALUE_FIELDS) > populated(US_VALUE_FIELDS)
    ? "metric"
    : "us";
};

const Pill = ({
  label,
  tone = "gray",
}: {
  label: string;
  tone?: "gray" | "green" | "blue";
}) => {
  const cls =
    tone === "green"
      ? "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300"
      : tone === "blue"
      ? "bg-penn-50 dark:bg-penn-900/30 text-penn-700 dark:text-penn-100"
      : "bg-gray-100 dark:bg-gray-800 text-blue_grey";
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-0.5 text-small-medium ${cls}`}>
      {label}
    </span>
  );
};

export const SherexFastenersPage = ({
  title = "Sherex Fasteners",
  subtitle = "Dataset: Sherex Fasteners",
}: {
  title?: string;
  subtitle?: string;
}) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const { searchInput, setSearchInput, search, clearSearch } = useDebouncedSearch(() => setPageIndex(0));

  const params = useMemo(
    () => ({
      page: pageIndex + 1,
      limit: pageSize,
      search: search || undefined,
    }),
    [pageIndex, pageSize, search],
  );

  const { data, isLoading, isFetching, isError, error } =
    useSherexFasteners(params);
  const rows: SherexFastener[] = data?.data ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const hasNext = pagination ? pagination.page < pagination.totalPages : false;

  const optionsQuery = useSherexFastenerOptions();
  const opts = optionsQuery.data;
  type ModalMode = "add" | "edit" | "view";
  type ModalState = { mode: ModalMode; item?: SherexFastener } | null;

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<SherexFastener | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const editingPartNumber = modal?.mode !== "add" ? modal?.item?.partNumber ?? null : null;
  const detail = useSherexFastener(editingPartNumber);

  const createMutation = useCreateSherexFastener();
  const updateMutation = useUpdateSherexFastener();
  const deleteMutation = useDeleteSherexFastener();

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<SherexFormData>({
    resolver: zodResolver(sherexFormSchema),
    defaultValues: blank,
  });

  const unitSystem = watch("unitSystem");

  const fillForm = (item: SherexFastener) => {
    // Form fields are typed `number | ""`. Some live RDS rows store
    // non-numeric strings (e.g. "06-32"); those map to "" so the
    // <input type="number"> stays valid.
    const num = (v: string | null): number | "" => {
      if (v === null || v === "") return "";
      const n = Number(v);
      return Number.isFinite(n) ? n : "";
    };
    reset({
      partNumber: item.partNumber,
      rivetNutType: item.rivetNutType ?? "",
      series: item.series ?? "",
      material: item.material ?? "",
      endType: item.endType ?? "",
      unitSystem: detectUnitSystem(item),
      diameterUs: item.diameterUs ?? "",
      diameterMetric: item.diameterMetric ?? "",
      minHoleSizeUs: num(item.minHoleSizeUs),
      minHoleSizeMetric: num(item.minHoleSizeMetric),
      maxHoleSizeUs: num(item.maxHoleSizeUs),
      maxHoleSizeMetric: num(item.maxHoleSizeMetric),
      minGripRangeUs: num(item.minGripRangeUs),
      minGripRangeMetric: num(item.minGripRangeMetric),
      maxGripRangeUs: num(item.maxGripRangeUs),
      maxGripRangeMetric: num(item.maxGripRangeMetric),
      bodyLengthUs: num(item.bodyLengthUs),
      bodyLengthMetric: num(item.bodyLengthMetric),
      fullLengthUs: num(item.fullLengthUs),
      fullLengthMetric: num(item.fullLengthMetric),
      flangeDiameterUs: num(item.flangeDiameterUs),
      flangeDiameterMetric: num(item.flangeDiameterMetric),
      flangeThicknessUs: num(item.flangeThicknessUs),
      flangeThicknessMetric: num(item.flangeThicknessMetric),
      installedLengthUs: num(item.installedLengthUs),
      installedLengthMetric: num(item.installedLengthMetric),
      shearStrengthUsLbf: num(item.shearStrengthUsLbf),
      shearStrengthMetricKn: num(item.shearStrengthMetricKn),
      tensileStrengthUsLbf: num(item.tensileStrengthUsLbf),
      tensileStrengthMetricKn: num(item.tensileStrengthMetricKn),
      radialDeflectionInch: num(item.radialDeflectionInch),
      radialDeflectionMm: num(item.radialDeflectionMm),
      tools: { ...item.tools },
      minRecommendedInstallForceLbs: num(item.minRecommendedInstallForceLbs),
      maxRecommendedInstallForceLbs: num(item.maxRecommendedInstallForceLbs),
    });
  };

  const openAdd = () => {
    setFormError(null);
    reset(blank);
    setModal({ mode: "add" });
  };
  const openEdit = (item: SherexFastener) => {
    setFormError(null);
    fillForm(item);
    setModal({ mode: "edit", item });
  };
  const openView = (item: SherexFastener) => {
    setFormError(null);
    fillForm(item);
    setModal({ mode: "view", item });
  };
  const closeModal = () => {
    setModal(null);
    setFormError(null);
    reset(blank);
  };

  const isView = modal?.mode === "view";
  const isAdd = modal?.mode === "add";

  const toStr = (v: string | number | undefined): string | null =>
    v === "" || v === undefined ? null : String(v);

  const onSubmit = (form: SherexFormData) => {
    if (!modal || isView) return;
    setFormError(null);

    const body = {
      rivetNutType: form.rivetNutType?.trim() || null,
      series: form.series?.trim() || null,
      material: (form.material || null) as SherexMaterial | null,
      endType: (form.endType || null) as SherexEndType | null,
      diameterUs: toStr(form.diameterUs),
      diameterMetric: toStr(form.diameterMetric),
      minHoleSizeUs: toStr(form.minHoleSizeUs),
      minHoleSizeMetric: toStr(form.minHoleSizeMetric),
      maxHoleSizeUs: toStr(form.maxHoleSizeUs),
      maxHoleSizeMetric: toStr(form.maxHoleSizeMetric),
      minGripRangeUs: toStr(form.minGripRangeUs),
      minGripRangeMetric: toStr(form.minGripRangeMetric),
      maxGripRangeUs: toStr(form.maxGripRangeUs),
      maxGripRangeMetric: toStr(form.maxGripRangeMetric),
      bodyLengthUs: toStr(form.bodyLengthUs),
      bodyLengthMetric: toStr(form.bodyLengthMetric),
      fullLengthUs: toStr(form.fullLengthUs),
      fullLengthMetric: toStr(form.fullLengthMetric),
      flangeDiameterUs: toStr(form.flangeDiameterUs),
      flangeDiameterMetric: toStr(form.flangeDiameterMetric),
      flangeThicknessUs: toStr(form.flangeThicknessUs),
      flangeThicknessMetric: toStr(form.flangeThicknessMetric),
      installedLengthUs: toStr(form.installedLengthUs),
      installedLengthMetric: toStr(form.installedLengthMetric),
      shearStrengthUsLbf: toStr(form.shearStrengthUsLbf),
      shearStrengthMetricKn: toStr(form.shearStrengthMetricKn),
      tensileStrengthUsLbf: toStr(form.tensileStrengthUsLbf),
      tensileStrengthMetricKn: toStr(form.tensileStrengthMetricKn),
      radialDeflectionInch: toStr(form.radialDeflectionInch),
      radialDeflectionMm: toStr(form.radialDeflectionMm),
      tools: form.tools,
      minRecommendedInstallForceLbs: toStr(form.minRecommendedInstallForceLbs),
      maxRecommendedInstallForceLbs: toStr(form.maxRecommendedInstallForceLbs),
    };

    if (modal.mode === "edit" && modal.item) {
      updateMutation.mutate(
        { partNumber: modal.item.partNumber, body },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to save sherex fastener.")),
        },
      );
    } else {
      createMutation.mutate(
        { partNumber: form.partNumber.trim(), ...body },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to create sherex fastener.")),
        },
      );
    }
  };

  const cols: ColDef<SherexFastener>[] = [
    { header: "Part Number", render: (i) => i.partNumber },
    { header: "Series", render: (i) => i.series ?? "—" },
    {
      header: "Rivet Nut Type",
      render: (i) => (i.rivetNutType ? <Pill label={i.rivetNutType} tone="blue" /> : "—"),
    },
    {
      header: "Material",
      render: (i) => (i.material ? <Pill label={MATERIAL_LABELS[i.material]} tone="green" /> : "—"),
    },
    {
      header: "End Type",
      render: (i) => (i.endType ? <Pill label={END_TYPE_LABELS[i.endType]} /> : "—"),
    },
    { header: "Force Min (lbs)", render: (i) => i.minRecommendedInstallForceLbs ?? "—" },
    { header: "Force Max (lbs)", render: (i) => i.maxRecommendedInstallForceLbs ?? "—" },
    {
      header: "Actions",
      render: (i) => (
        <div className="flex space-x-2">
          <ViewButton onClick={() => openView(i)} />
          <EditButton onClick={() => openEdit(i)} />
          <DeleteButton onClick={() => setDeleteTarget(i)} />
        </div>
      ),
    },
  ];

  const saving = createMutation.isPending || updateMutation.isPending;
  const titleMap: Record<ModalMode, string> = {
    add: "Add Sherex Fasteners Record",
    edit: "Edit Sherex Fasteners Record",
    view: "View Sherex Fasteners Record",
  };

  // ---- Modal content (extracted only for readability) ----
  const dimSuffix = unitSystem === "us" ? "Us" : "Metric";
  const lenUnit = unitSystem === "us" ? "in" : "mm";
  const forceUnit = unitSystem === "us" ? "lbf" : "kN";
  const radialUnit = unitSystem === "us" ? "inch" : "mm";

  // Diameter is a thread designation (text), not a numeric dimension.
  const diameterField = () => {
    const fieldName = (
      unitSystem === "us" ? "diameterUs" : "diameterMetric"
    ) as keyof SherexFormData;
    return (
      <PennField
        label="Diameter"
        htmlFor={fieldName}
        error={!isView ? (errors as Record<string, { message?: string }>)[fieldName]?.message : undefined}
      >
        <PennInput
          id={fieldName}
          type="text"
          disabled={isView}
          {...register(fieldName as never)}
        />
      </PennField>
    );
  };

  const dimensionField = (
    label: string,
    baseName:
      | "minHoleSize"
      | "maxHoleSize"
      | "minGripRange"
      | "maxGripRange"
      | "bodyLength"
      | "fullLength"
      | "flangeDiameter"
      | "flangeThickness"
      | "installedLength",
  ) => {
    const fieldName = (baseName + dimSuffix) as keyof SherexFormData;
    return (
      <PennField
        label={`${label} (${lenUnit})`}
        htmlFor={fieldName}
        error={!isView ? (errors as Record<string, { message?: string }>)[fieldName]?.message : undefined}
      >
        <PennInput
          id={fieldName}
          type="number"
          step="0.001"
          disabled={isView}
          {...register(fieldName as never)}
        />
      </PennField>
    );
  };

  const strengthField = (
    label: string,
    base: "shearStrength" | "tensileStrength",
  ) => {
    const fieldName = (base + (unitSystem === "us" ? "UsLbf" : "MetricKn")) as keyof SherexFormData;
    return (
      <PennField
        label={`${label} (${forceUnit})`}
        htmlFor={fieldName}
        error={!isView ? (errors as Record<string, { message?: string }>)[fieldName]?.message : undefined}
      >
        <PennInput
          id={fieldName}
          type="number"
          step="0.001"
          disabled={isView}
          {...register(fieldName as never)}
        />
      </PennField>
    );
  };

  const radialField = () => {
    const fieldName = (unitSystem === "us" ? "radialDeflectionInch" : "radialDeflectionMm") as keyof SherexFormData;
    return (
      <PennField
        label={`Radial Deflection (${radialUnit})`}
        htmlFor={fieldName}
        error={!isView ? (errors as Record<string, { message?: string }>)[fieldName]?.message : undefined}
      >
        <PennInput
          id={fieldName}
          type="number"
          step="0.001"
          disabled={isView}
          {...register(fieldName as never)}
        />
      </PennField>
    );
  };

  return (
    <>
      <PageHeader
        title={title}
        subtitle={`${subtitle} · ${total} records`}
        action={<AddButton onClick={openAdd} />}
      />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load sherex fasteners
          {error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      {deleteError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          {deleteError}
        </div>
      )}

      <TableSearch
        value={searchInput}
        onChange={setSearchInput}
        onClear={clearSearch}
        placeholder="Search series, rivet nut type, or part number…"
      />

      <AdminTable
        columns={cols}
        rows={rows}
        keyExtractor={(i) => i.partNumber}
        loading={isLoading}
      />

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={rows.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />

      <AdminModal
        open={modal !== null}
        onClose={closeModal}
        title={modal ? titleMap[modal.mode] : ""}
        maxWidth="max-w-3xl"
        footer={
          isView ? (
            <CancelButton onClick={closeModal} />
          ) : (
            <>
              <CancelButton onClick={closeModal} />
              <SaveButton onClick={handleSubmit(onSubmit)} label={saving ? "Saving…" : "Save"} />
            </>
          )
        }
      >
        {modal?.mode !== "add" && detail.isLoading ? (
          <div className="py-10 text-center text-blue_grey4 text-body">Loading…</div>
        ) : (
          <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
            {formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
                {formError}
              </div>
            )}

            {/* Identity */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <PennField
                label="Part Number"
                htmlFor="partNumber"
                required={isAdd}
                error={isAdd ? errors.partNumber?.message : undefined}
              >
                <PennInput
                  id="partNumber"
                  disabled={!isAdd}
                  error={isAdd && !!errors.partNumber}
                  {...register("partNumber")}
                />
              </PennField>

              <PennField label="Rivet Nut Type" htmlFor="rivetNutType">
                <Controller
                  control={control}
                  name="rivetNutType"
                  render={({ field }) => (
                    <PennDropdown
                      inputId="rivetNutType"
                      options={(opts?.rivetNutTypes ?? []).map((v) => ({ value: v, label: v }))}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      isDisabled={isView}
                    />
                  )}
                />
              </PennField>

              <PennField label="Series" htmlFor="series">
                <Controller
                  control={control}
                  name="series"
                  render={({ field }) => (
                    <PennDropdown
                      inputId="series"
                      options={(opts?.series ?? []).map((v) => ({ value: v, label: v }))}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      isDisabled={isView}
                    />
                  )}
                />
              </PennField>

              <PennField label="Material" htmlFor="material">
                <Controller
                  control={control}
                  name="material"
                  render={({ field }) => (
                    <PennDropdown
                      inputId="material"
                      options={(opts?.materials ?? []).map((m) => ({ value: m, label: MATERIAL_LABELS[m] }))}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      isDisabled={isView}
                      placeholder="—"
                    />
                  )}
                />
              </PennField>
            </div>

            {/* FASTENER DETAILS */}
            <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4 space-y-4">
              <p className="uppercase text-blue_grey3 text-small-medium">
                Fastener Details
              </p>

              {/* Unit system radio */}
              <div className="flex items-center gap-6">
                <span className="text-blue_grey text-body-medium">
                  Unit System
                </span>
                {(isAdd || unitSystem === "us") && (
                  <label className="inline-flex items-center gap-2 text-body">
                    <input
                      type="radio"
                      value="us"
                      disabled={!isAdd}
                      {...register("unitSystem")}
                    />
                    US
                  </label>
                )}
                {(isAdd || unitSystem === "metric") && (
                  <label className="inline-flex items-center gap-2 text-body">
                    <input
                      type="radio"
                      value="metric"
                      disabled={!isAdd}
                      {...register("unitSystem")}
                    />
                    Metric
                  </label>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {diameterField()}
                {dimensionField("Min Hole Size", "minHoleSize")}
                {dimensionField("Max Hole Size", "maxHoleSize")}
                {dimensionField("Min Grip Range", "minGripRange")}
                {dimensionField("Max Grip Range", "maxGripRange")}
                {dimensionField("Body Length", "bodyLength")}
                {dimensionField("Full Length", "fullLength")}
                {dimensionField("Flange Diameter", "flangeDiameter")}
                {dimensionField("Flange Thickness", "flangeThickness")}
                {dimensionField("Installed Length", "installedLength")}
                {strengthField("Shear Strength", "shearStrength")}
                {strengthField("Tensile Strength", "tensileStrength")}
                <div className="col-span-1 sm:col-span-2">{radialField()}</div>
              </div>

              {/* Blind / Open */}
              <div className="flex items-center gap-6">
                <span className="text-blue_grey text-body-medium">
                  Blind / Open
                </span>
                <label className="inline-flex items-center gap-2 text-body">
                  <input type="radio" value="blind" disabled={isView} {...register("endType")} />
                  Blind
                </label>
                <label className="inline-flex items-center gap-2 text-body">
                  <input type="radio" value="open" disabled={isView} {...register("endType")} />
                  Open
                </label>
              </div>
            </div>

            {/* Installation Tools */}
            <PennField label="Installation Tool Recommended" htmlFor="tools">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {TOOL_KEYS.map((key) => (
                  <Controller
                    key={key}
                    control={control}
                    name={`tools.${key}` as const}
                    render={({ field }) => (
                      <label className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-body">
                        <input
                          type="checkbox"
                          disabled={isView}
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                        {TOOL_LABELS[key]}
                      </label>
                    )}
                  />
                ))}
              </div>
            </PennField>

            {/* Force */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <PennField
                label="Minimum Recommended Installation Force (lbs)"
                htmlFor="minRecommendedInstallForceLbs"
                error={!isView ? errors.minRecommendedInstallForceLbs?.message : undefined}
              >
                <PennInput
                  id="minRecommendedInstallForceLbs"
                  type="number"
                  step="0.01"
                  disabled={isView}
                  error={!isView && !!errors.minRecommendedInstallForceLbs}
                  {...register("minRecommendedInstallForceLbs")}
                />
              </PennField>
              <PennField
                label="Maximum Recommended Installation Force (lbs)"
                htmlFor="maxRecommendedInstallForceLbs"
                error={!isView ? errors.maxRecommendedInstallForceLbs?.message : undefined}
              >
                <PennInput
                  id="maxRecommendedInstallForceLbs"
                  type="number"
                  step="0.01"
                  disabled={isView}
                  error={!isView && !!errors.maxRecommendedInstallForceLbs}
                  {...register("maxRecommendedInstallForceLbs")}
                />
              </PennField>
            </div>
          </form>
        )}
      </AdminModal>

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          setDeleteError(null);
          deleteMutation.mutate(deleteTarget.partNumber, {
            onError: (e) =>
              setDeleteError(apiErrorMessage(e, "Failed to delete sherex fastener.")),
            onSettled: () => setDeleteTarget(null),
          });
        }}
        itemLabel={deleteTarget?.partNumber}
      />
    </>
  );
};
